<template>
  <AdminLayout>
    <Head title="Edit Package" />

    <template #pageAlerts>
      <SuccessFlash :flash="flash"></SuccessFlash>
      <ErrorFlash
        v-if="hasErrors()"
        :message="'An error has occurred'"
      ></ErrorFlash>
      <ErrorFlash
        v-if="getErrors('coa', { errors })"
        :message="getErrors('coa', { errors })"
      ></ErrorFlash>
    </template>

    <PanelGroup class="flex-1">
      <div class="w-full overflow-auto flex flex-col flex-1">
        <form-request
          :action="computedAction"
          :method="computedMethod"
          @failure="updateFailed"
          v-slot="{ updateForm, errors, submit }"
        >
          <span
            v-if="editing"
            class="bg-blue-600 uppercase text-sm p-2 text-gray-100 text-center font-semibold mx-4 sm:mx-12 rounded-lg mb-6"
          >
            Editing Package
          </span>

          <form-controls
            :title="packageTitle"
            :package-id="package.id"
            :subtitle="productName"
            :editing="editing"
            :is-published="isPublished"
            :is-archived="isArchived"
            :nova-route="novaRoute"
            @save="saveChanges(submit)"
            @edit="toggleEdit"
            @publish="openPublish"
            @unpublish="openUnpublish"
            @transfer="openTransfer"
            @subLot="openSubLot"
            @label="openLabel"
            @showlabels="openShowLabel"
            @adjust="openAdjust"
            @balance="openBalance"
            @assign="openAssign"
            @audit="goToAudit"
            @archive="openArchive"
            @restore="openRestore"
            class="mx-4 sm:mx-12"
          />

          <!-- Quantity aggregate metrics -->
          <dl
            class="mb-5 flex sm:rounded-lg bg-gray-100 overflow-y-hidden overflow-x-scroll shadow divide-gray-400 divide-x sm:mx-12"
          >
            <div class="shrink-0 w-9/12 sm:flex-1 px-4 py-5 sm:p-6">
              <dt class="text-base font-semibold text-blue-600">Total Produced in Batch</dt>
              <dd class="mt-1 flex justify-between items-baseline md:block lg:flex">
                <div class="flex items-baseline text-2xl font-semibold text-blue-600">
                  {{ package.quantity }}
                </div>
              </dd>
            </div>
            <div class="shrink-0 w-9/12 sm:flex-1 px-4 py-5 sm:p-6">
              <dt class="text-base font-semibold text-blue-600">Available for Sale</dt>
              <dd class="mt-1 flex justify-between items-baseline md:block lg:flex">
                <div class="flex items-baseline text-2xl font-semibold text-blue-600">
                  {{ package.quantity_available_for_sale }}
                </div>
              </dd>
            </div>
            <div class="shrink-0 w-9/12 sm:flex-1 px-4 py-5 sm:p-6">
              <dt class="text-base font-semibold text-blue-600">Total at {{ defaultLocationName || 'Source' }}</dt>
              <dd class="mt-1 flex justify-between items-baseline md:block lg:flex">
                <div class="flex items-baseline text-2xl font-semibold text-blue-600">
                  {{ package.quantity_available_at_source }}
                </div>
              </dd>
            </div>
            <div class="shrink-0 w-9/12 sm:flex-1 px-4 py-5 sm:p-6">
              <dt class="text-base font-semibold text-blue-600">Transfers Pending</dt>
              <dd class="mt-1 flex justify-between items-baseline md:block lg:flex">
                <div class="flex items-baseline text-2xl font-semibold text-blue-600">
                  {{ package.quantity_transfers_pending }}
                </div>
              </dd>
            </div>
            <div class="shrink-0 w-9/12 sm:flex-1 px-4 py-5 sm:p-6">
              <dt class="text-base font-semibold text-blue-600">Transfers In-Transit</dt>
              <dd class="mt-1 flex justify-between items-baseline md:block lg:flex">
                <div class="flex items-baseline text-2xl font-semibold text-blue-600">
                  {{ package.quantity_transfers_in_transit }}
                </div>
              </dd>
            </div>
          </dl>
          <!-- END Quantity aggregate metrics -->

          <PageTabGroup>
            <PageTabList>
              <PageTab label="Details" />
              <PageTab label="Media" />
              <PageTab label="Pricing" />
              <PageTab label="Locations" />
              <PageTab label="Transfers" />
              <PageTab
                v-if="hasPermission('view_activity')"
                label="Activity Log"
              />
            </PageTabList>

            <PageTabPanels>
              <PageTabPanel>
                <details-form
                  :package="package"
                  :errors="errors"
                  :disabled="!editing"
                  :products="products"
                  :lineages="lineages"
                  @update:modelValue="updateForm"
                />
              </PageTabPanel>

              <PageTabPanel>
                <media-manager
                  :files="images"
                  :upload="mediaUploadRoute"
                  :featured="mediaFeaturedRoute"
                  :destroy="mediaDeleteRoute"
                  collection-name="media"
                  upload-key="media"
                  class="mb-8"
                />

                <media-manager
                  :files="coas"
                  :upload="mediaUploadRoute"
                  :featured="mediaFeaturedRoute"
                  :destroy="mediaDeleteRoute"
                  collection-name="coas"
                  upload-key="coas"
                  title="COA Manager"
                  description="Upload pdf COA's associated with this package"
                />
              </PageTabPanel>

              <PageTabPanel>
                <pricing-form
                  :src="updatePricingRoute"
                  :package="package"
                />
              </PageTabPanel>

              <PageTabPanel
                without-padding
                locked-height
              >
                <!--  @Todo: Rename component later. What are these props for? -->
                <locations-form
                  :package-title="packageTitle"
                  :package-id="package.id"
                  :package-locations="package.locations || []"
                />
              </PageTabPanel>

              <PageTabPanel>
                <transfers-form :transfers="transfers" />
              </PageTabPanel>

              <PageTabPanel
                without-padding
                locked-height
              >
                <ActivityLog
                  :resource-type="LOG_TYPES.PACKAGE"
                  :resource-id="package.id"
                />
              </PageTabPanel>
            </PageTabPanels>
          </PageTabGroup>
        </form-request>
      </div>

      <template #right>
        <SpaceMovementPanel />
      </template>
    </PanelGroup>

    <template #modals>
      <!-- modal forms -->
      <assign-product
        :show="currentModal === 'assign'"
        :src="assignProductRoute"
        :products="products"
        @close="closeAllModals"
      />
      <transfer-package
        :show="currentModal === 'transfer'"
        :src="transferPackageRoute"
        :locations="locations"
        :available="package.quantity_available"
        @close="closeAllModals"
      />
      <create-sub-lot-modal
        :show="currentModal === 'subLot'"
        :package="package"
        :src="createSubLotRoute"
        :locations="locations"
        :spaces="spaces"
        @close="closeAllModals"
      />
      <show-label
        :show="currentModal === 'showLabel'"
        :package="package"
        :modes="labelModes"
        @close="closeAllModals"
      />
      <print-label
        :show="currentModal === 'label'"
        :packages="[package]"
        :locations="locations"
        :modes="labelModes"
        @close="closeAllModals"
      />
      <adjust-package
        :show="currentModal === 'adjust'"
        :src="adjustPackageRoute"
        :package="package"
        :locations="locations"
        :spaces="spaces"
        :adjustment-reasons="adjustmentReasons"
        @close="closeAllModals"
      />
      <balance-package
        :show="currentModal === 'balance'"
        :src="balancePackageRoute"
        :package="package"
        :integrations="adjustmentIntegrations"
        :locations="locations"
        :spaces="spaces"
        @close="closeAllModals"
      />
    </template>
  </AdminLayout>
</template>

<script setup>
import { getCurrentInstance } from 'vue'
import { useInitGridState } from '@/Components/DataGrid/Composables/grid-state'
import { PageTabGroup, PageTabList, PageTab, PageTabPanels, PageTabPanel } from '@/Components/PageTabs'
import { PanelGroup } from '@/Components/Panels'
import SpaceMovementPanel from '@/Pages/Packages/Components/SpaceMovementPanel.vue'
import CreateSubLotModal from '@/Pages/Packages/Components/CreateSubLotModal.vue'

const instance = getCurrentInstance()
const { context } = useInitGridState()
</script>

<script>
import AdminLayout from '@/Layouts/AdminLayout.vue'
import { Head, useForm } from '@inertiajs/vue3'
import SuccessFlash from '@/Partials/Alerts/SuccessFlash'
import ErrorFlash from '@/Partials/Alerts/ErrorFlash'
import DetailsForm from './Form/_details'
import MediaManager from '@/Components/Media/MediaManager'
import PricingForm from './Form/_pricing'
import TransfersForm from './Form/_transfers'
import LocationsForm from './Form/_locations'
import FormControls from './Form/FormControls'
import FormRequest from '@/Components/Forms/FormRequest'
import PrintLabel from '@/Pages/Packages/Components/PrintLabel'
import ShowLabel from '@/Pages/Packages/Components/ShowLabel'
import TransferPackage from '@/Pages/Packages/Components/TransferPackage'
import AdjustPackage from '@/Pages/Packages/Components/AdjustPackage'
import BalancePackage from '@/Pages/Packages/Components/BalancePackage.vue'
import AssignProduct from '@/Pages/Packages/Components/AssignProduct'
import checksErrors from '@/Mixins/checksErrors'
import accessesCompany from '@/Mixins/accessesCompany'
import ActivityLog from '@/Components/ActivityLog/ActivityLog.vue'
import { LOG_TYPES } from '@/Components/ActivityLog/log-types'
import hasAuth from '@/Mixins/hasAuth'
import { useConfirmDialog } from '@/Composables/confirm-dialog'
import { settle } from '@/utils/promises'

export default {
  components: {
    AssignProduct,
    PrintLabel,
    TransferPackage,
    AdjustPackage,
    BalancePackage,
    FormRequest,
    FormControls,
    DetailsForm,
    MediaManager,
    PricingForm,
    TransfersForm,
    LocationsForm,
    AdminLayout,
    Head,
    SuccessFlash,
    ErrorFlash,
    ActivityLog
  },

  mixins: [hasAuth, checksErrors, accessesCompany],

  props: {
    /**
     * The package model retrieved from the database
     */
    package: {
      type: Object,
      required: true
    },
    locations: {
      type: Array,
      default: () => []
    },
    adjustmentIntegrations: {
      type: Array,
      default: () => []
    },
    spaces: {
      type: Array,
      default: () => []
    },
    products: {
      type: Array,
      default: () => []
    },
    company: {
      type: Object,
      required: true
    },
    /**
     * A list of files attached to this package
     */
    media: {
      type: Array,
      default: () => []
    },
    /**
     * A list of transfers that belong to this package
     */
    transfers: {
      type: Array,
      default() {
        return []
      }
    },
    /**
     * The url we send the edited form to for saving
     */
    updateRoute: {
      type: String,
      default: null
    },
    /**
     * Flash messages from the server
     */
    flash: {
      default: null
    },
    /**
     * A key val list of validation errors
     */
    errors: {
      type: Object,
      default: () => {}
    },
    /**
     * A key val list of lineages for selection
     */
    lineages: {
      type: Object,
      default() {
        return {}
      }
    },
    /**
     * A list of label models, array of objects like { value: 'sku', label: 'SKU' }
     */
    labelModes: {
      type: Object,
      default: () => {}
    },
    subLotNums: {
      type: Array,
      default: () => []
    },
    adjustmentReasons: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      LOG_TYPES,
      editing: false,
      currentModal: null,
      selectedTab: 'details'
    }
  },

  computed: {
    /**
     * The server http route we send form data
     */
    computedAction() {
      return this.updateRoute
    },

    /**
     * The server http method we use to send form data (post, patch, get, etc)
     */
    computedMethod() {
      return 'patch'
    },

    /**
     * The url to upload files to
     * @returns String
     */
    mediaUploadRoute() {
      return route('packages.media.store', { package: this.package.id })
    },

    /**
     * The url to send a transfer request to
     * @returns String
     */
    printLabelRoute() {
      return route('packages.print_label', { package: this.package.id })
    },

    /**
     * The url to send a transfer request to
     * @returns String
     */
    transferPackageRoute() {
      return route('package.transfer', { package: this.package.id })
    },

    /**
     * The url to send a split sub lot request to
     * @returns String
     */
    createSubLotRoute() {
      return route('package.sublot', { package: this.package.id })
    },

    /**
     * The url to send an adjustment request to
     * @returns String
     */
    adjustPackageRoute() {
      return route('package.adjust', { package: this.package.id })
    },

    /**
     * The url to send an adjustment request to
     * @returns String
     */
    balancePackageRoute() {
      return route('package.balance', { package: this.package.id })
    },

    /**
     * The url to send a pricing override to
     * @returns String
     */
    updatePricingRoute() {
      return route('package.pricing', { package: this.package.id })
    },

    /**
     * The url to send a delete package request
     * @returns String
     */
    archiveRoute() {
      return route('packages.destroy', { package: this.package.id })
    },

    /**
     * The url to send a delete package request
     * @returns String
     */
    restoreRoute() {
      return route('packages.restore', { package: this.package.id })
    },

    /**
     * The url to send a publish package request
     * @returns String
     */
    publishRoute() {
      return route('package.publish', { package: this.package.id })
    },

    /**
     * The url to send a un-publish package request
     * @returns String
     */
    unpublishRoute() {
      return route('package.unpublish', { package: this.package.id })
    },

    /**
     * The url to send an 'assign product' request to
     * @returns String
     */
    assignProductRoute() {
      return route('package.assign', { package: this.package.id })
    },

    /**
     * Returns a function that can be used to obtain the correct route for deleting files
     * @returns {(function(*))|*}
     */
    mediaDeleteRoute() {
      let thePackage = this.package
      let theCompany = this.company
      return (file) => {
        return route('packages.media.destroy', {
          package: thePackage.id,
          company: theCompany.slug,
          media: file.id
        })
      }
    },

    /**
     * Returns a function that can be used to set a file as the "featured" image
     * @returns {(function(*))|*}
     */
    mediaFeaturedRoute() {
      let thePackage = this.package
      let theCompany = this.company
      return (file) => {
        return route('package.images.featured', {
          package: thePackage.id,
          company: theCompany.slug,
          image: file.id
        })
      }
    },

    packageTitle() {
      return this.package.name || this.package.batch_num
    },

    productName() {
      return this.package.product ? this.package.product.name : null
    },

    /**
     * A list of files that belong to the package that we will display in the media manager
     * @returns {*|*[]}
     */
    files() {
      return this.media || []
    },

    /**
     * A list of files that belong to the package that we will display in the media manager
     * @returns {*|*[]}
     */
    images() {
      return this.files.filter((file) => {
        return file.collection_name === 'media'
      })
    },

    /**
     * A list of files that belong to the package that we will display in the media manager
     * @returns {*|*[]}
     */
    coas() {
      return this.files.filter((file) => {
        return file.collection_name === 'coas'
      })
    },

    isPublished() {
      return this.package.published_at !== null
    },

    isArchived() {
      return this.package.deleted_at !== null
    },

    novaRoute() {
      return route('nova.pages.detail', {
        resource: 'packages',
        resourceId: this.package.id
      })
    }
  },

  methods: {
    toggleEdit() {
      this.editing = !this.editing
    },

    /**
     * Submits the changed form to the server
     * @param submit function (from form-request component) that performs the http request
     */
    saveChanges(submit) {
      this.editing = false
      submit()
    },

    /**
     * Called if the data passed to server for updating the package failed validation
     */
    updateFailed() {
      // Re open edit form
      this.editing = true
    },

    /**
     * Called when the currently selected tab changes
     * @param tab the name of the tab we changed to
     */
    changeTab(tab) {
      this.selectedTab = tab
    },

    /**
     * Returns true if argument is same string as currently selected tab
     * @param tab (string) name of tab we want to check
     * @returns {boolean}
     */
    isSelectedTab(tab) {
      return tab === this.selectedTab
    },
    openTransfer() {
      this.currentModal = 'transfer'
    },
    openSubLot() {
      this.currentModal = 'subLot'
    },
    openLabel() {
      this.currentModal = 'label'
    },
    openShowLabel() {
      this.currentModal = 'showLabel'
    },
    openAdjust() {
      this.currentModal = 'adjust'
    },
    openBalance() {
      this.currentModal = 'balance'
    },
    openAssign() {
      this.currentModal = 'assign'
    },
    async openArchive() {
      const { show } = useConfirmDialog()
      const [error, data] = await settle(
        show({
          type: 'danger',
          title: 'Archive Product',
          body: 'Are you sure you want to delete this package?',
          confirmText: 'Archive'
        })
      )
      if (!error) {
        useForm({}).delete(this.archiveRoute)
      }
    },
    async openRestore() {
      const { show } = useConfirmDialog()
      const [error, data] = await settle(
        show({
          type: 'danger',
          title: 'Restore Product',
          body: 'Are you sure you want to restore this package?',
          confirmText: 'Restore'
        })
      )
      if (!error) {
        useForm({}).post(this.restoreRoute)
      }
    },
    closeAllModals() {
      this.currentModal = null
    },

    goToAudit() {
      if (!this.package.product_id) {
        return
      }

      const url = route('products.balance-entries.index', {
        product: this.package.product_id,
        package_id: this.package.id
      })
      this.$inertia.visit(url)
    }
  }
}
</script>
